import css from "./Vendors.module.css";

/**
 * ONE EXPANDABLE SECTION OF THE VENDOR RECORD.
 *
 * The record used to be a long scroll of always-open cards with four bare
 * "▶ Payouts" strips tacked on underneath — two different ideas of a section
 * on one page, and the strips read as broken rather than as closed. Every
 * part of the record is now this: a header that says what is inside and
 * whether it needs looking at (so the page can be read closed), and a body
 * that opens under it.
 *
 * `tone` colours the icon and the left rail: ok / warn / danger / info /
 * neutral. `meta` is the short status on the right — a count or a flag.
 */
export const SEC_ICONS = {
  profile: (
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  ),
  agreement: (
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 15l2 2 4-4" />
  ),
  listings: (
    <path d="M3 9h18M5 9V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9M9 13h6" />
  ),
  submissions: (
    <path d="M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  ),
  payouts: (
    <path d="M2 7h20v12H2zM2 11h20M6 15h4" />
  ),
  orders: (
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />
  ),
  subscriptions: (
    <path d="M21 12a9 9 0 0 1-15.5 6.2M3 12A9 9 0 0 1 18.5 5.8M21 4v5h-5M3 20v-5h5" />
  ),
  customers: (
    <path d="M3 21V7l9-4 9 4v14M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01" />
  ),
};

export default function VendorSection({
  id,
  icon,
  title,
  summary,
  meta,
  tone = "neutral",
  open,
  onToggle,
  children,
}) {
  const bodyId = `vsec-${id}`;
  return (
    <section
      className={`${css.sec} ${css[`secTone_${tone}`] || ""} ${open ? css.secOpen : ""}`}
    >
      <button
        type="button"
        className={css.secHead}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className={css.secIco} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {SEC_ICONS[icon] || SEC_ICONS.listings}
          </svg>
        </span>
        <span className={css.secText}>
          <span className={css.secTitle}>{title}</span>
          {summary && <span className={css.secSum}>{summary}</span>}
        </span>
        {meta && <span className={css.secMeta}>{meta}</span>}
        <span className={css.secChev} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && (
        <div id={bodyId} className={css.secBody}>
          {children}
        </div>
      )}
    </section>
  );
}
